import { Avatar } from '@elementary/common-ui/components';
import {
  INCIDENT_DETAILS,
  INCIDENT_FILTERS,
  OPEN_INCIDENTS,
  ACKNOWLEDGED_INCIDENTS,
  USERS,
  RESOLVED_INCIDENTS,
} from '@elementary/common-ui/constants/queryKeys';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchAssignees,
  fetchIncidentFilters,
  updateIncident,
} from 'incidents/api';
import { Incident } from 'incidents/types';
import { severityIconMap, statusIconMap } from 'incidents/utils';
import useEnvironments from 'shared/hooks/useEnvironments';
import { ReactComponent as UserIcon } from '@elementary/common-ui/assets/16x16/user.svg';
import { NO_ASSIGNEE_VALUE } from 'incidents/constants';

type UpdateIncidentParams = {
  incidentId: string;
  assignee?: string;
  status?: string;
  severity?: string;
};

const useIncidentMetadataOptions = () => {
  const { selectedEnvId } = useEnvironments();
  const hasPermission = useEnvPermission();
  const queryClient = useQueryClient();

  const { data: incidentFilters } = useQuery(
    [INCIDENT_FILTERS, selectedEnvId],
    () => fetchIncidentFilters(selectedEnvId!),
  );

  const { data: usersData } = useQuery([USERS], () =>
    fetchAssignees(selectedEnvId!),
  );

  const { mutateAsync: updateIncidentMutation, isLoading: isUpdating } =
    useMutation(
      async ({
        incidentId,
        assignee,
        status,
        severity,
      }: UpdateIncidentParams) => {
        if (selectedEnvId) {
          return updateIncident(selectedEnvId, incidentId, {
            assignee,
            status,
            severity,
          });
        }
      },
      {
        onSuccess: (_, { incidentId }) => {
          queryClient.invalidateQueries([OPEN_INCIDENTS], { exact: false });
          queryClient.invalidateQueries([ACKNOWLEDGED_INCIDENTS], {
            exact: false,
          });
          queryClient.invalidateQueries([RESOLVED_INCIDENTS], { exact: false });
          queryClient.invalidateQueries([INCIDENT_DETAILS, incidentId]);
        },
      },
    );

  const canEdit = hasPermission('EDIT_INCIDENTS');

  const severityOptions = incidentFilters?.options
    .find((f) => f.property === 'severity')
    ?.values.map((v) => ({
      label: v,
      value: v,
      icon: severityIconMap[v as Incident['severity']],
    }));

  const statusOptions = incidentFilters?.options
    .find((f) => f.property === 'status')
    ?.values.map((v) => ({
      label: v,
      value: v,
      icon: statusIconMap[v as Incident['status']],
    }));

  const assigneeOptions = usersData
    ?.sort((a, b) => a.name.localeCompare(b.name))
    .map((u) => ({
      label: u.name,
      value: u.email,
      icon: <Avatar value={{ id: u.id, name: u.name }} size="small" />,
    }))
    ?.concat([
      {
        label: 'Unassigned',
        value: NO_ASSIGNEE_VALUE,
        icon: <UserIcon />,
      },
    ]);

  return {
    incidentFilters,
    usersData,
    severityOptions,
    statusOptions,
    assigneeOptions,
    canEdit,
    updateIncident: updateIncidentMutation,
    isUpdating,
  };
};

export default useIncidentMetadataOptions;
